import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  List,
  Avatar,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  LinearProgress,
  Fab,
  Tabs,
  Tab,
  Checkbox,
  Menu,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ro } from 'date-fns/locale';
import {
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  LocalHospital as DoctorIcon,
  Schedule as ScheduleIcon,
  Phone as PhoneIcon,
  MoreVert as MoreIcon,
  Login as CheckInIcon,
  Message as MessageIcon,
  AccessTime as PendingIcon,
  CheckCircle as ConfirmedIcon,
  LocalHospital as CompletedIcon,
  Cancel as CancelledIcon,
  Event as DefaultIcon,
  ContentCopy as CopyIcon,
  WhatsApp as WhatsAppIcon,
  Visibility as ViewIcon,
  CalendarViewMonth as CalendarMonthIcon,
  ViewList as ListIcon,
  Note as NoteIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { appointmentsService } from '@services/appointmentsService';
import AppointmentsCalendar, { CalendarAppointment } from './AppointmentsCalendar';

// Types
interface BaseAppointment {
  id: string;
  date: string;
  time: string;
  patientName: string;
  patientSpecies: string;
  patientBreed?: string;
  patientAge?: number;
  clientName: string;
  clientPhone: string;
  clientEmail?: string;
  doctorName: string;
  service: string;
  status: 'pending_confirmation' | 'confirmed' | 'scheduled' | 'checked_in' | 'in_progress' | 'completed' | 'no_show' | 'cancelled';
  location: string;
  notes?: string;
  isUrgent?: boolean;
  duration: number;
}

interface Appointment extends BaseAppointment {
  doctorId?: string;
  source?: 'online' | 'phone' | 'facebook' | 'walk_in';
  visitType?: 'clinic' | 'home' | 'online';
  price?: number;
  assignedRoom?: string;
  assignedEquipment?: string[];
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
  confirmedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  reminders?: {
    sms: boolean;
    email: boolean;
    whatsapp: boolean;
  };
}

interface RescheduleDialogState {
  open: boolean;
  appointment: Appointment | null;
  date: string;
  time: string;
  reason: string;
}

interface FilterState {
  search: string;
  status: string;
  doctor: string;
  location: string;
  source: string;
  visitType: string;
  period: string;
  customFrom?: Date;
  customTo?: Date;
  tags: string[];
  urgentOnly: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`appointments-tabpanel-${index}`}
      aria-labelledby={`appointments-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Status Badge Component with colors and legend
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'confirmed':
        return { color: 'success', bgcolor: '#E8F5E8', textColor: '#2E7D32', icon: <ConfirmedIcon />, label: 'Confirmata' };
      case 'pending_confirmation':
        return { color: 'warning', bgcolor: '#FFF6D5', textColor: '#8A6D00', icon: <PendingIcon />, label: 'In asteptare' };
      case 'scheduled':
        return { color: 'info', bgcolor: '#E3F2FD', textColor: '#1976D2', icon: <ScheduleIcon />, label: 'Programata' };
      case 'completed':
        return { color: 'success', bgcolor: '#E8F5E8', textColor: '#2E7D32', icon: <CompletedIcon />, label: 'Finalizata' };
      case 'cancelled':
        return { color: 'error', bgcolor: '#FFEBEE', textColor: '#D32F2F', icon: <CancelledIcon />, label: 'Anulata' };
      case 'no_show':
        return { color: 'default', bgcolor: '#F5F5F5', textColor: '#616161', icon: <DefaultIcon />, label: 'No-show' };
      case 'checked_in':
      case 'in_progress':
        return { color: 'info', bgcolor: '#E1F5FE', textColor: '#0277BD', icon: <CheckInIcon />, label: 'Check-in' };
      default:
        return { color: 'default', bgcolor: '#F5F5F5', textColor: '#616161', icon: <DefaultIcon />, label: status };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size="small"
      sx={{
        bgcolor: config.bgcolor,
        color: config.textColor,
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 24,
        '& .MuiChip-icon': {
          color: 'inherit',
          fontSize: '0.9rem'
        }
      }}
    />
  );
};

// Quick Actions Component
const QuickActions: React.FC<{
  appointment: Appointment;
  onAction: (action: string, appointment: Appointment) => void;
}> = ({ appointment, onAction }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: string) => {
    onAction(action, appointment);
    handleClose();
  };

  const canConfirm = appointment.status === 'pending_confirmation';
  const canCancel = ['pending_confirmation', 'confirmed', 'scheduled'].includes(appointment.status);
  const canCheckIn = appointment.status === 'confirmed' && new Date(appointment.date) <= new Date();
  const canReschedule = ['pending_confirmation', 'confirmed', 'scheduled'].includes(appointment.status);
  const canNoShow = ['confirmed', 'scheduled', 'in_progress'].includes(appointment.status);
  const canConvertToVisit = ['pending_confirmation', 'confirmed', 'scheduled', 'in_progress'].includes(appointment.status);

  return (
    <>
      <IconButton size="small" onClick={handleClick}>
        <MoreIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {canConfirm && (
          <MenuItem onClick={() => handleAction('confirm')}>
            <CheckIcon sx={{ mr: 1 }} />
            Confirma
          </MenuItem>
        )}
        {canCheckIn && (
          <MenuItem onClick={() => handleAction('checkin')}>
            <CheckInIcon sx={{ mr: 1 }} />
            Check-in
          </MenuItem>
        )}
        {canReschedule && (
          <MenuItem onClick={() => handleAction('reschedule')}>
            <ScheduleIcon sx={{ mr: 1 }} />
            Reprogrameaza
          </MenuItem>
        )}
        {canNoShow && (
          <MenuItem onClick={() => handleAction('no_show')}>
            <WarningIcon sx={{ mr: 1 }} />
            Marcheaza No-show
          </MenuItem>
        )}
        {canConvertToVisit && (
          <MenuItem onClick={() => handleAction('convert_to_visit')}>
            <DoctorIcon sx={{ mr: 1 }} />
            Converteste in vizita
          </MenuItem>
        )}
        {canCancel && (
          <MenuItem onClick={() => handleAction('cancel')}>
            <CancelIcon sx={{ mr: 1 }} />
            Anuleaza
          </MenuItem>
        )}
        <MenuItem onClick={() => handleAction('notes')} disabled>
          <NoteIcon sx={{ mr: 1 }} />
          Note (indisponibil)
        </MenuItem>
        <MenuItem onClick={() => handleAction('invoice')} disabled>
          <ReceiptIcon sx={{ mr: 1 }} />
          Factura (indisponibil)
        </MenuItem>
        <MenuItem onClick={() => handleAction('message')} disabled>
          <MessageIcon sx={{ mr: 1 }} />
          Mesaj (indisponibil)
        </MenuItem>
        <MenuItem onClick={() => handleAction('view')}>
          <ViewIcon sx={{ mr: 1 }} />
          Vezi detalii
        </MenuItem>
      </Menu>
    </>
  );
};

const AppointmentsManagementEnhanced: React.FC = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedAppointments, setSelectedAppointments] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: 'all',
    doctor: 'all',
    location: 'all',
    source: 'all',
    visitType: 'all',
    period: 'week',
    tags: [],
    urgentOnly: false
  });
  
  // Dialog states
  const [_bulkActionsDialogOpen, setBulkActionsDialogOpen] = useState(false);
  const [_filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  const [_appointmentDetailDialogOpen, setAppointmentDetailDialogOpen] = useState(false);
  const [_selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [rescheduleDialog, setRescheduleDialog] = useState<RescheduleDialogState>({
    open: false,
    appointment: null,
    date: '',
    time: '',
    reason: '',
  });
  
  // Real API data - no mock data

  // Load appointments
  const loadAppointments = useCallback(async () => {
    if (!user?.clinicId) return;
    
    setLoading(true);
    try {
      // Real API call to get appointments for the clinic
      const response = await appointmentsService.getAppointments({ clinicId: user.clinicId });
      const data = response.appointments;

      // Transform API data to match our interface
      const transformedAppointments: Appointment[] = data.map((apt: any) => ({
        id: apt.id,
        date: apt.scheduledAt ? new Date(apt.scheduledAt).toISOString().split('T')[0] : '',
        time: apt.scheduledAt ? new Date(apt.scheduledAt).toLocaleTimeString('ro-RO', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }) : '',
        patientName: apt.animalName || 'Necunoscut',
        patientSpecies: apt.animalSpecies || 'Necunoscut',
        patientBreed: apt.animalBreed || apt.breed,
        patientAge: apt.animalAge || apt.age,
        clientName: apt.ownerFirstName && apt.ownerLastName 
          ? `${apt.ownerFirstName} ${apt.ownerLastName}` 
          : apt.clientEmail || 'Necunoscut',
        clientPhone: apt.clientPhone || '',
        clientEmail: apt.clientEmail,
        doctorName: apt.veterinarianFirstName && apt.veterinarianLastName
          ? `Dr. ${apt.veterinarianFirstName} ${apt.veterinarianLastName}`
          : 'Nealocat',
        doctorId: apt.veterinarianId,
        service: apt.serviceName || apt.reason || 'Serviciu necunoscut',
        status: apt.status,
        location: apt.clinicName || 'Locatie necunoscuta',
        notes: apt.notes || '',
        isUrgent: apt.emergency || false,
        source: apt.source || 'online',
        visitType: apt.visitType || 'clinic',
        duration: apt.duration || 30,
        price: apt.price,
        assignedRoom: apt.assignedRoom,
        assignedEquipment: apt.assignedEquipment,
        tags: apt.tags || [],
        createdAt: apt.created_at,
        updatedAt: apt.updated_at,
        confirmedAt: apt.confirmedAt,
        completedAt: apt.completedAt,
        cancelledAt: apt.cancelledAt,
        cancellationReason: apt.cancellationReason,
        reminders: {
          sms: apt.reminders?.sms || false,
          email: apt.reminders?.email || false,
          whatsapp: apt.reminders?.whatsapp || false
        }
      }));

      setAppointments(transformedAppointments);
    } catch (error) {
      console.error('Eroare la incarcarea programarilor:', error);
      enqueueSnackbar('Eroare la incarcarea programarilor', { variant: 'error' });
      setAppointments([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [user?.clinicId, enqueueSnackbar]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Filter appointments
  const filteredAppointments = appointments.filter(appointment => {
    const matchesSearch = !filters.search || 
      appointment.patientName.toLowerCase().includes(filters.search.toLowerCase()) ||
      appointment.clientName.toLowerCase().includes(filters.search.toLowerCase()) ||
      appointment.clientPhone.includes(filters.search) ||
      appointment.doctorName.toLowerCase().includes(filters.search.toLowerCase()) ||
      appointment.service.toLowerCase().includes(filters.search.toLowerCase()) ||
      appointment.id.includes(filters.search);
    
    const matchesStatus = filters.status === 'all' || appointment.status === filters.status;
    const matchesDoctor = filters.doctor === 'all' || appointment.doctorId === filters.doctor;
    const matchesLocation = filters.location === 'all' || appointment.location === filters.location;
    const matchesSource = filters.source === 'all' || appointment.source === filters.source;
    const matchesVisitType = filters.visitType === 'all' || appointment.visitType === filters.visitType;
    const matchesUrgent = !filters.urgentOnly || appointment.isUrgent;
    
    return matchesSearch && matchesStatus && matchesDoctor && matchesLocation && 
           matchesSource && matchesVisitType && matchesUrgent;
  });

  // Group appointments by period
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const todayAppointments = filteredAppointments.filter(apt => {
    const aptDate = new Date(apt.date);
    aptDate.setHours(0, 0, 0, 0);
    return aptDate.getTime() === today.getTime();
  });
  
  const upcomingAppointments = filteredAppointments.filter(apt => {
    const aptDate = new Date(apt.date);
    aptDate.setHours(0, 0, 0, 0);
    return aptDate > today;
  });
  
  const pastAppointments = filteredAppointments.filter(apt => {
    const aptDate = new Date(apt.date);
    aptDate.setHours(0, 0, 0, 0);
    return aptDate < today;
  });

  // Handle actions
  const handleAppointmentAction = async (action: string, appointment: Appointment) => {
    try {
      switch (action) {
      case 'confirm':
        await appointmentsService.updateAppointmentStatus(appointment.id, 'confirmed');
        enqueueSnackbar(`Programarea pentru ${appointment.patientName} a fost confirmata`, { variant: 'success' });
        loadAppointments(); // Reload to get updated data
        break;
      case 'cancel':
        await appointmentsService.cancelAppointment(appointment.id, 'Anulata din ecranul clinic appointments');
        enqueueSnackbar(`Programarea pentru ${appointment.patientName} a fost anulata`, { variant: 'warning' });
        loadAppointments(); // Reload to get updated data
        break;
      case 'checkin':
        await appointmentsService.checkIn(appointment.id);
        enqueueSnackbar(`${appointment.patientName} s-a prezentat la clinica`, { variant: 'success' });
        loadAppointments(); // Reload to get updated data
        break;
      case 'reschedule':
        setRescheduleDialog({
          open: true,
          appointment,
          date: appointment.date,
          time: appointment.time,
          reason: '',
        });
        break;
      case 'no_show':
        await appointmentsService.markNoShow(appointment.id);
        enqueueSnackbar(`Programarea pentru ${appointment.patientName} a fost marcata no-show`, { variant: 'warning' });
        loadAppointments();
        break;
      case 'convert_to_visit': {
        const result = await appointmentsService.convertToVisit(appointment.id);
        enqueueSnackbar(`Programarea pentru ${appointment.patientName} a fost convertita in vizita`, { variant: 'success' });
        navigate(`/visits/${result.visitId}/encounter`);
        break;
      }
      case 'message':
        enqueueSnackbar('Actiune indisponibila in flow-ul oficial appointments', { variant: 'info' });
        break;
      case 'notes':
        enqueueSnackbar('Actiune indisponibila in flow-ul oficial appointments', { variant: 'info' });
        break;
      case 'invoice':
        enqueueSnackbar('Actiune indisponibila in flow-ul oficial appointments', { variant: 'info' });
        break;
      case 'view':
        setSelectedAppointment(appointment);
        setAppointmentDetailDialogOpen(true);
        break;
      }
    } catch (error) {
      console.error(`Error performing ${action} action:`, error);
      enqueueSnackbar(`Eroare la ${action}`, { variant: 'error' });
    }
  };

  const handleSubmitReschedule = async () => {
    if (!rescheduleDialog.appointment) return;
    if (!rescheduleDialog.date || !rescheduleDialog.time || !rescheduleDialog.reason.trim()) {
      enqueueSnackbar('Completeaza data, ora si motivul reprogramarii', { variant: 'warning' });
      return;
    }
    try {
      await appointmentsService.rescheduleAppointment(
        rescheduleDialog.appointment.id,
        rescheduleDialog.date,
        rescheduleDialog.time,
        rescheduleDialog.reason.trim(),
      );
      enqueueSnackbar(`Programarea pentru ${rescheduleDialog.appointment.patientName} a fost reprogramata`, { variant: 'success' });
      setRescheduleDialog({ open: false, appointment: null, date: '', time: '', reason: '' });
      loadAppointments();
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      enqueueSnackbar('Eroare la reprogramare', { variant: 'error' });
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedAppointments.length === 0) return;
    try {
      const ok = await appointmentsService.bulkAction({ action, ids: selectedAppointments });
      if (!ok) throw new Error('bulk_action_failed');
      enqueueSnackbar(`Actiunea ${action} a fost aplicata pentru ${selectedAppointments.length} programari`, { variant: 'success' });
      setSelectedAppointments([]);
      setBulkActionsDialogOpen(false);
      loadAppointments();
    } catch (error) {
      console.error(`Error performing bulk ${action}:`, error);
      enqueueSnackbar(`Eroare la actiunea bulk: ${action}`, { variant: 'error' });
    }
  };

  const handlePhoneClick = (phone: string) => {
    window.open(`tel:${phone}`, '_self');
  };

  const handleWhatsAppClick = (phone: string) => {
    const message = encodeURIComponent('Buna! Va contactez in legatura cu programarea la clinica veterinara.');
    window.open(`https://wa.me/${phone.replace(/^0/, '40')}?text=${message}`, '_blank');
  };

  const handleCopyPhone = (phone: string) => {
    navigator.clipboard.writeText(phone);
    enqueueSnackbar('Numarul de telefon a fost copiat', { variant: 'success' });
  };

  // Calendar handlers
  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setAppointmentDetailDialogOpen(true);
  };

  const handleAppointmentDrag = async (appointmentId: string, newDate: string, newTime: string) => {
    try {
      await appointmentsService.rescheduleAppointment(
        appointmentId,
        newDate,
        newTime,
        'Reprogramare din calendar'
      );
      enqueueSnackbar('Programarea a fost reprogramata', { variant: 'success' });
      loadAppointments();
    } catch (error) {
      console.error('Error reprogramming appointment from calendar:', error);
      enqueueSnackbar('Eroare la reprogramarea din calendar', { variant: 'error' });
    }
  };

  const handleDateClick = (date: Date) => {
    enqueueSnackbar(
      `Data selectata: ${date.toLocaleDateString('ro-RO')}. Crearea rapida din calendar nu este disponibila inca.`,
      { variant: 'warning' }
    );
  };

  // Render appointment card
  const renderAppointmentCard = (appointment: Appointment, showCheckbox: boolean = false) => (
    <Card
      key={appointment.id}
      sx={{
        mb: 2,
        borderLeft: appointment.isUrgent ? '6px solid' : '6px solid transparent',
        borderLeftColor: appointment.isUrgent ? 'error.main' : 'transparent',
        '&:hover': {
          boxShadow: 3,
          '& .quick-actions': { opacity: 1 }
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {showCheckbox && (
            <Checkbox
              checked={selectedAppointments.includes(appointment.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedAppointments([...selectedAppointments, appointment.id]);
                } else {
                  setSelectedAppointments(selectedAppointments.filter(id => id !== appointment.id));
                }
              }}
            />
          )}
          
          <Avatar sx={{ bgcolor: 'primary.light' }}>
            {appointment.patientSpecies === 'Caine' ? '🐶' : 
             appointment.patientSpecies === 'Pisica' ? '🐱' : '🐾'}
          </Avatar>
          
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                {appointment.time} - {appointment.patientName}
              </Typography>
              {appointment.isUrgent && (
                <Chip label="URGENT" color="error" size="small" />
              )}
              <StatusBadge status={appointment.status} />
            </Box>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Pacient:</strong> {appointment.patientName} ({appointment.patientSpecies.toLowerCase()}, {appointment.patientAge} ani)
              {appointment.patientBreed && ` • ${appointment.patientBreed}`}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Client:</strong> {appointment.clientName} • 
              <Tooltip title="Suna">
                <IconButton size="small" onClick={() => handlePhoneClick(appointment.clientPhone)}>
                  <PhoneIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="WhatsApp">
                <IconButton size="small" onClick={() => handleWhatsAppClick(appointment.clientPhone)}>
                  <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copiaza numarul">
                <IconButton size="small" onClick={() => handleCopyPhone(appointment.clientPhone)}>
                  <CopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {appointment.clientPhone}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Doctor:</strong> {appointment.doctorName} • <strong>Serviciu:</strong> {appointment.service}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Locatie:</strong> {appointment.location} • <strong>Durata:</strong> {appointment.duration} min
              {appointment.assignedRoom && ` • Sala: ${appointment.assignedRoom}`}
            </Typography>
            
            {appointment.notes && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                <strong>Note:</strong> {appointment.notes}
              </Typography>
            )}
            
            {appointment.tags && appointment.tags.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                {appointment.tags.map((tag, index) => (
                  <Chip key={index} label={tag} size="small" variant="outlined" />
                ))}
              </Box>
            )}
          </Box>
          
          <Box className="quick-actions" sx={{ opacity: 0, transition: 'opacity 0.2s' }}>
            <QuickActions appointment={appointment} onAction={handleAppointmentAction} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se incarca programarile...
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Programari
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Gestioneaza programarile clinicii - calendar complet cu statusuri si alerte
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<CalendarMonthIcon />}
              onClick={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}
            >
              {viewMode === 'list' ? 'Calendar' : 'Lista'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled
              onClick={() => enqueueSnackbar('Crearea din acest ecran este indisponibila in flow-ul oficial', { variant: 'info' })}
              sx={{ borderRadius: 2 }}
            >
              Programare Noua (indisponibil)
            </Button>
          </Stack>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 2, alignItems: 'center' }}>
              <TextField
                fullWidth
                placeholder="Cauta dupa pacient, client, telefon, doctor, serviciu, ID..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="pending_confirmation">In asteptare</MenuItem>
                  <MenuItem value="confirmed">Confirmate</MenuItem>
                  <MenuItem value="scheduled">Programate</MenuItem>
                  <MenuItem value="completed">Finalizate</MenuItem>
                  <MenuItem value="cancelled">Anulate</MenuItem>
                  <MenuItem value="no_show">No-show</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Doctor</InputLabel>
                <Select
                  value={filters.doctor}
                  onChange={(e) => setFilters({...filters, doctor: e.target.value})}
                  label="Doctor"
                >
                  <MenuItem value="all">Toti doctorii</MenuItem>
                  <MenuItem value="doc1">Dr. Maria Popescu</MenuItem>
                  <MenuItem value="doc2">Dr. Ana Ionescu</MenuItem>
                  <MenuItem value="unassigned">Nealocat</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Locatie</InputLabel>
                <Select
                  value={filters.location}
                  onChange={(e) => setFilters({...filters, location: e.target.value})}
                  label="Locatie"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="Clinica principala">Clinica principala</MenuItem>
                  <MenuItem value="Sucursala 1">Sucursala 1</MenuItem>
                  <MenuItem value="Sucursala 2">Sucursala 2</MenuItem>
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1}>
                <IconButton onClick={() => setFiltersDialogOpen(true)}>
                  <FilterIcon />
                </IconButton>
                <IconButton onClick={loadAppointments}>
                  <RefreshIcon />
                </IconButton>
              </Stack>
            </Box>
            
            {/* Period Filter */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: filters.period === 'custom' ? 'repeat(3, 1fr)' : '1fr' }, gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Perioada</InputLabel>
                <Select
                  value={filters.period}
                  onChange={(e) => setFilters({...filters, period: e.target.value})}
                  label="Perioada"
                >
                  <MenuItem value="today">Astazi</MenuItem>
                  <MenuItem value="week">Saptamana</MenuItem>
                  <MenuItem value="month">Luna</MenuItem>
                  <MenuItem value="custom">Interval custom</MenuItem>
                </Select>
              </FormControl>
              {filters.period === 'custom' && (
                <>
                  <TextField
                    label="De la"
                    type="date"
                    value={filters.customFrom ? filters.customFrom.toISOString().split('T')[0] : ''}
                    onChange={(e) => setFilters({...filters, customFrom: e.target.value ? new Date(e.target.value) : undefined})}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Pana la"
                    type="date"
                    value={filters.customTo ? filters.customTo.toISOString().split('T')[0] : ''}
                    onChange={(e) => setFilters({...filters, customTo: e.target.value ? new Date(e.target.value) : undefined})}
                    InputLabelProps={{ shrink: true }}
                  />
                </>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedAppointments.length > 0 && (
          <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {selectedAppointments.length} programari selectate
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => handleBulkAction('confirm')}
                  >
                    Confirma toate
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => handleBulkAction('cancel')}
                  >
                    Anuleaza toate
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => setSelectedAppointments([])}
                  >
                    Deselecteaza
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Status Legend */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Legenda statusuri
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <StatusBadge status="confirmed" />
              <StatusBadge status="pending_confirmation" />
              <StatusBadge status="scheduled" />
              <StatusBadge status="completed" />
              <StatusBadge status="cancelled" />
              <StatusBadge status="no_show" />
            </Stack>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card sx={{ borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListIcon />
                    Lista
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarMonthIcon />
                    Calendar
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Astazi
                    <Badge badgeContent={todayAppointments.length} color="primary" />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Viitoare
                    <Badge badgeContent={upcomingAppointments.length} color="secondary" />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Trecute
                    <Badge badgeContent={pastAppointments.length} color="default" />
                  </Box>
                } 
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            {filteredAppointments.length > 0 ? (
              <List>
                {filteredAppointments.map((appointment) => 
                  renderAppointmentCard(appointment, false)
                )}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <ListIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nu ai programari care sa corespunda filtrelor
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => enqueueSnackbar('Crearea din acest ecran este indisponibila in flow-ul oficial', { variant: 'info' })}
                  disabled
                >
                  Creeaza prima programare
                </Button>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <AppointmentsCalendar
              appointments={filteredAppointments as CalendarAppointment[]}
              onAppointmentClick={(appointment: CalendarAppointment) => handleAppointmentClick(appointment as Appointment)}
              onAppointmentDrag={handleAppointmentDrag}
              onDateClick={handleDateClick}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {todayAppointments.length > 0 ? (
              <List>
                {todayAppointments.map((appointment) => 
                  renderAppointmentCard(appointment, false)
                )}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CalendarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nu ai programari pentru astazi
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => enqueueSnackbar('Crearea din acest ecran este indisponibila in flow-ul oficial', { variant: 'info' })}
                  disabled
                >
                  Creeaza prima programare
                </Button>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {upcomingAppointments.length > 0 ? (
              <List>
                {upcomingAppointments.map((appointment) => 
                  renderAppointmentCard(appointment, false)
                )}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nu ai programari viitoare
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => enqueueSnackbar('Crearea din acest ecran este indisponibila in flow-ul oficial', { variant: 'info' })}
                  disabled
                >
                  Creeaza prima programare
                </Button>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            {pastAppointments.length > 0 ? (
              <List>
                {pastAppointments.map((appointment) => 
                  renderAppointmentCard(appointment, false)
                )}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nu ai programari trecute
                </Typography>
              </Box>
            )}
          </TabPanel>
        </Card>

        <Dialog
          open={rescheduleDialog.open}
          onClose={() => setRescheduleDialog({ open: false, appointment: null, date: '', time: '', reason: '' })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reprogrameaza programarea</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Data noua"
                type="date"
                value={rescheduleDialog.date}
                onChange={(e) => setRescheduleDialog(prev => ({ ...prev, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Ora noua"
                type="time"
                value={rescheduleDialog.time}
                onChange={(e) => setRescheduleDialog(prev => ({ ...prev, time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Motiv reprogramare"
                value={rescheduleDialog.reason}
                onChange={(e) => setRescheduleDialog(prev => ({ ...prev, reason: e.target.value }))}
                multiline
                minRows={3}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRescheduleDialog({ open: false, appointment: null, date: '', time: '', reason: '' })}>
              Anuleaza
            </Button>
            <Button onClick={handleSubmitReschedule} variant="contained">
              Salveaza
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sticky FAB for new appointment */}
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' }
          }}
          onClick={() => enqueueSnackbar('Crearea din acest ecran este indisponibila in flow-ul oficial', { variant: 'info' })}
        >
          <AddIcon />
        </Fab>
      </Box>
    </LocalizationProvider>
  );
};

export default AppointmentsManagementEnhanced;
